import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirConsolidationTbService } from './fir-consolidation-tb.service';

@Component({
    selector: 'gtw-fir-consolidation-tb',
    templateUrl: './fir-consolidation-tb.component.html',
    styleUrls: ['./fir-consolidation-tb.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirConsolidationTbComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subscriptions = new Subscription();
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private gridLoadSeq = 0;
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  modalTitle = 'FIR GAAP Amount';
  loading = false;
  showFormLoader = true;
  minimise = false;
  gridData: any[] = [];
  gridHeaders: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private consolidationService: FirConsolidationTbService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.gridLoadSeq++;
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.gridLoadSeq++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private resolveBaseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.loadGridData();
  }

  private loadGridData() {
    const baseUrl = this.resolveBaseUrl();
    if (!baseUrl) {
      this.loading = false;
      this.showFormLoader = false;
      this.gtwSnackbarService.openSnackBar(
        'Error occurred fetching data. If this continues please contact support',
        'gtw-snackbar--red'
      );
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.showFormLoader = true;
    this.cdr.detectChanges();

    const firHoLeidFilter = {
      HO_LEID: this.rowData.HO_LEID,
      HO_CDR_NO: this.rowData.HO_CDR_NO,
      HO_REPORTING_PERIOD: this.rowData.HO_REPORTING_PERIOD
    };

    const gp = extractObject(this.globalService?.globalParams);
    const params = {
      tax_year: this.filterParams.tax_year || gp.tax_year,
      scenario: this.filterParams.scenario || gp.scenario,
      jcd_key: gp.jcd_key || 250,
      screen_key: gp.screen_key,
      filing_key: this.filterParams.filing_key || gp.filing_key,
      fir_ho_leid: JSON.stringify(firHoLeidFilter)
    };

    const seq = ++this.gridLoadSeq;

    this.subscriptions.add(
      this.consolidationService.loadGridData(
        baseUrl,
        'jktdjh',
        293,
        params
      ).subscribe(
        (data: any) => {
          if (seq !== this.gridLoadSeq) {
            return;
          }
          if (data.callSuccess === '1') {
            this.gridData = data.data || [];
            this.gridHeaders = data.header || [];
          } else {
            this.gtwSnackbarService.openSnackBar(
              'Error occurred fetching data. If this continues please contact support',
              'gtw-snackbar--red'
            );
          }
          this.loading = false;
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          if (seq !== this.gridLoadSeq) {
            return;
          }
          console.error('Error loading grid data:', error);
          this.loading = false;
          this.showFormLoader = false;
          this.cdr.detectChanges();
        }
      )
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
